export function hasQuarterComparison(comparisons) {
  return Array.isArray(comparisons)
    && Number.isFinite(comparisons[2]?.value)
    && comparisons.slice(0, 2).some(period => Number.isFinite(period?.value));
}

export const SERIES_COLORS = ['#2e9d91', '#a879e8', '#ee9851', '#5796ed', '#d55f92', '#94a63e'];

export function comparisonMetrics(data) {
  return [
    ...(data?.statements || []).flatMap((statement, s) => statement.rows.map((row, r) => ({
      ...row, id: `${s}:${r}`, kind: statement.kind,
      group: { income: 'Income statement', balance_sheet: 'Balance sheet', cash_flow: 'Cash flow' }[statement.kind] || statement.title,
      source_url: statement.source_url,
    }))),
    ...(data?.comparison_metrics || []).map(metric => ({ ...metric, group: 'Calculated metrics' })),
  ].filter(metric => hasQuarterComparison(metric.comparisons));
}

export function canUseAxis(series, metric, axis) {
  return series.every(item => item.id === metric.id || item.axis !== axis || item.unit === metric.unit);
}

export function addComparisonMetric(series, metric) {
  if (series.some(item => item.id === metric.id) || series.length >= SERIES_COLORS.length) return series;
  const axis = ['left', 'right'].find(side => canUseAxis(series, metric, side));
  if (!axis) return series;
  const color = SERIES_COLORS.find(candidate => !series.some(item => item.color === candidate));
  return [...series, { ...metric, type: 'bar', axis, color }];
}

// Both axes include zero, with aligned zero lines even when one series is negative.
export function comparisonScales(series) {
  const extents = Object.fromEntries(['left', 'right'].map(axis => {
    const values = series.filter(item => item.axis === axis).flatMap(item => item.comparisons
      .map(period => period.value).filter(Number.isFinite));
    return [axis, { positive: Math.max(0, ...values), negative: Math.max(0, ...values.map(v => -v)) }];
  }));
  const intervals = 5;
  const hasPositive = Object.values(extents).some(({ positive }) => positive > 0);
  const negativeShare = Math.max(...Object.values(extents).map(({ positive, negative }) => negative / (positive + negative || 1)));
  const negativeIntervals = negativeShare === 0 ? 0 : !hasPositive ? intervals
    : Math.max(1, Math.min(intervals - 1, Math.round(negativeShare * intervals)));
  const positiveIntervals = intervals - negativeIntervals;
  return Object.fromEntries(Object.entries(extents).map(([axis, { positive, negative }]) => {
    const requiredStep = Math.max(positive / (positiveIntervals || 1), negative / (negativeIntervals || 1)) * 1.05 || 1;
    const magnitude = 10 ** Math.floor(Math.log10(requiredStep));
    const step = [1, 2, 2.5, 5, 10].find(factor => factor * magnitude >= requiredStep) * magnitude;
    const min = -step * negativeIntervals;
    const max = step * positiveIntervals;
    const ticks = Array.from({ length: intervals + 1 }, (_, index) => Number((max - step * index).toPrecision(12)));
    return [axis, { min, max, ticks, y: value => 280 - (value - min) / (max - min) * 230 }];
  }));
}

export function comparisonAxisLabel(series, axis) {
  const metrics = series.filter(item => item.axis === axis);
  if (metrics.length === 1) return metrics[0].label;
  if (metrics.every(item => item.type === 'bar')) return 'Bar Chart Metrics';
  if (metrics.every(item => item.type === 'line')) return 'Line Chart Metrics';
  return 'Chart Metrics';
}
